/** @format */
import { ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, Signal, ViewEncapsulation } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ValidatablePlan } from 'common-typescript';
import { CourseUnit, CourseUnitAttainment, StudyPeriodLocator } from 'common-typescript/types';
import { AppErrorHandler } from 'sis-components';
import { StudentCourseUnitInfoModalService } from '../../../../../common/service/student-course-unit-info-modal.service';
import { SelectedValidatablePlanService } from '../../../../service/selected-validatable-plan.service';

@Component({
    selector: 'app-timeline-course-unit',
    standalone: false,
    templateUrl: './timeline-course-unit.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimelineCourseUnitComponent {
    readonly courseUnit: InputSignal<CourseUnit> = input.required<CourseUnit>();
    readonly headingLevel: InputSignal<number> = input.required<number>();

    private readonly appErrorHandler = inject(AppErrorHandler);
    private readonly selectedValidatablePlanService = inject(SelectedValidatablePlanService);
    private readonly studentCourseUnitInfoModalService = inject(StudentCourseUnitInfoModalService);

    private readonly validatablePlan: Signal<ValidatablePlan | null> = toSignal(
        this.selectedValidatablePlanService.selectedValidatablePlan$.pipe(this.appErrorHandler.defaultErrorHandler()),
    );

    protected readonly attainment: Signal<CourseUnitAttainment | null> = computed(() =>
        this.courseUnit() && this.validatablePlan() && this.validatablePlan().isCourseUnitAttained(this.courseUnit().id)
            ? this.validatablePlan().getCourseUnitAttainment(this.courseUnit().id)
            : null,
    );

    /**
     * If the course unit is not attained but is selected in the plan,
     * returns locators of the planned study periods,
     * otherwise null.
     */
    protected readonly plannedPeriods: Signal<StudyPeriodLocator[] | null> = computed(
        () =>
            // trusting that ValidatablePlan.getPlannedPeriods doesn't return planned periods for attained course units
            this.validatablePlan()?.getPlannedPeriods(this.courseUnit()?.id) ?? null,
    );

    protected openCourseUnitInfoModal(): void {
        this.studentCourseUnitInfoModalService.openCourseUnitCompletionMethods(this.courseUnit().id, this.validatablePlan());
    }
}
